import React from 'react';
import { useIntersection } from 'react-use';
import { EventInteractions, EVENT_INTERACTION } from './constants';
import type { EventTrackingMetaData, TrackInteractionCallback } from './types';
import { logStatsigClick } from './utils';

const NULL_REF = { current: null };

export const useTrackImpression = (
    node: React.Ref<HTMLDivElement>,
    trackInteraction: TrackInteractionCallback,
) => {
    const isImpressed = React.useRef<boolean>(false);
    const intersectionRef = isImpressed.current ? NULL_REF : node;
    const intersection = useIntersection(intersectionRef, {
        threshold: 0.25,
        root: null,
    });

    if (
        intersection &&
        intersection.intersectionRatio > 0.25 &&
        !isImpressed.current
    ) {
        trackInteraction(EventInteractions.Impression);
        isImpressed.current = true;
    }
};

export const useTrackInteraction = (
    node: React.RefObject<HTMLDivElement>,
    eventMetaData: EventTrackingMetaData,
    statsigLocationString?: string,
) => {
    //Destructuring metaData blob to ensure
    //callback is more stable.
    const { isPromoted, isFree, isOnlineEvent, hasUrgencySignals, id } =
        eventMetaData;

    const trackingCallback = React.useCallback<TrackInteractionCallback>(
        (interaction) => {
            //Recompose metadata object
            const metaData = {
                isPromoted,
                isFree,
                isOnlineEvent,
                hasUrgencySignals,
                id,
            };

            //Statsig Click Tracking
            if (interaction === EventInteractions.Click) {
                statsigLocationString &&
                    logStatsigClick(statsigLocationString, metaData);
            }

            //Internal Tracking (event agnostic)
            node?.current?.dispatchEvent(
                new CustomEvent(EVENT_INTERACTION, {
                    detail: {
                        id: metaData.id,
                        action: interaction,
                        event: {
                            isPromoted,
                            isFree,
                            isOnline: isOnlineEvent,
                            hasUrgencySignals,
                        },
                    },
                    bubbles: true,
                }),
            );
        },

        [
            isPromoted,
            isFree,
            isOnlineEvent,
            hasUrgencySignals,
            id,
            node,
            statsigLocationString,
        ],
    );

    return trackingCallback;
};
